import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, openFile } from "@/lib/api";
import FolderDialog from "@/components/FolderDialog";
import { CompanyLogo } from "@/components/CompanyLogo";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Folder, CalendarClock, Users, FileLock2, Plus, Upload, Download,
  History, Trash2, UserPlus, Loader2, Building2, FolderPlus, Search, Pencil, ArrowRightLeft,
} from "lucide-react";

export default function CompanyDetail() {
  const { companyId } = useParams();
  const nav = useNavigate();
  const [company, setCompany] = useState(null);
  const [folders, setFolders] = useState([]);
  const [openFolder, setOpenFolder] = useState(null);
  const [folderEditor, setFolderEditor] = useState(null);
  const [folderTitle, setFolderTitle] = useState("");
  const [folderSaving, setFolderSaving] = useState(false);
  const [folderDeleting, setFolderDeleting] = useState(null);

  const [logoKey, setLogoKey] = useState(0);
  const loadCompany = () => api.get(`/companies/${companyId}`).then((r) => setCompany(r.data));
  const loadFolders = () => api.get(`/companies/${companyId}/folders`).then((r) => setFolders(r.data));
  useEffect(() => { loadCompany(); loadFolders(); /* eslint-disable-next-line */ }, [companyId]);

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/companies/${companyId}/logo`, fd); toast.success("Logo güncellendi"); setLogoKey((k) => k + 1); loadCompany(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const editFolder = (folder = null) => {
    setFolderTitle(folder?.title || "");
    setFolderEditor({ folder });
  };

  const saveFolder = async (event) => {
    event.preventDefault();
    if (!folderTitle.trim()) { toast.error("Klasör adı girin"); return; }
    setFolderSaving(true);
    try {
      const folder = folderEditor.folder;
      if (folder) {
        await api.put(`/companies/${companyId}/folders/${folder.index}`, { title: folderTitle.trim() });
      } else {
        await api.post(`/companies/${companyId}/folders`, { title: folderTitle.trim() });
      }
      toast.success(folder ? "Klasör adı güncellendi" : "Klasör oluşturuldu");
      setFolderEditor(null);
      await loadFolders();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setFolderSaving(false); }
  };

  const deleteFolder = async (folder) => {
    if (!window.confirm(`"${folder.title}" klasörü silinsin mi?`)) return;
    setFolderDeleting(folder.index);
    try {
      await api.delete(`/companies/${companyId}/folders/${folder.index}`);
      toast.success("Klasör silindi");
      await loadFolders();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setFolderDeleting(null); }
  };

  if (!company) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-emerald-600" /></div>;

  return (
    <div className="fade-in space-y-6">
      <button onClick={() => nav("/admin/firmalar")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="back-to-companies">
        <ArrowLeft className="h-4 w-4" /> Firmalar
      </button>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <CompanyLogo companyId={companyId} refreshKey={logoKey} className="h-full w-full object-contain"
            fallback={<div className="flex h-full w-full items-center justify-center bg-[#0a192f] text-white"><Building2 className="h-7 w-7" /></div>} />
        </div>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-[#0a192f]">{company.name}</h1>
          <p className="text-sm text-slate-500">{[company.contact_person, company.phone, company.email].filter(Boolean).join(" · ")}</p>
        </div>
        <label className="inline-flex" data-testid="upload-logo-label">
          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files[0])} data-testid="logo-file-input" />
          <span className="inline-flex cursor-pointer items-center rounded-md border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50">
            <Upload className="mr-1.5 h-4 w-4" /> {company.has_logo ? "Logoyu Değiştir" : "Logo Yükle"}
          </span>
        </label>
      </div>

      <Tabs defaultValue="folders">
        <TabsList className="flex-wrap">
          <TabsTrigger value="folders" data-testid="tab-folders"><Folder className="mr-1.5 h-4 w-4" />Klasörler</TabsTrigger>
          <TabsTrigger value="deadlines" data-testid="tab-deadlines"><CalendarClock className="mr-1.5 h-4 w-4" />Süre Takibi</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users"><Users className="mr-1.5 h-4 w-4" />Müşteriler</TabsTrigger>
          <TabsTrigger value="word" data-testid="tab-word"><FileLock2 className="mr-1.5 h-4 w-4" />Özel Dosya Alanı</TabsTrigger>
        </TabsList>

        <TabsContent value="folders" className="mt-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-500">Yeni klasörler yalnızca bu firmaya aittir. Müşteri yayımlanan belgeleri görür.</p>
            <Button onClick={() => editFolder()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-custom-folder">
              <FolderPlus className="mr-1.5 h-4 w-4" /> Yeni Klasör Oluştur
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {folders.map((f) => (
              <Card key={f.index} onClick={() => setOpenFolder(f)} data-testid={`folder-card-${f.index}`}
                className="group cursor-pointer p-4 transition-all hover:border-emerald-300 hover:shadow-md">
                <div className="flex items-start justify-between">
                  <Folder className="h-6 w-6 text-emerald-600" />
                  <span className="text-xs font-semibold text-slate-400">{f.is_custom ? "Firmaya özel" : `#${f.index}`}</span>
                </div>
                <h3 className="mt-3 line-clamp-2 min-h-[2.5rem] text-sm font-semibold text-[#0a192f]">{f.title}</h3>
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="text-slate-400">{f.published_count}/{f.total_count} yayımlı</span>
                  {f.deadline_type && <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-700">Süre</span>}
                </div>
                {f.is_custom && (
                  <div className="mt-3 flex gap-2 border-t border-slate-100 pt-3" onClick={(e) => e.stopPropagation()}>
                    <Button variant="outline" size="sm" onClick={() => editFolder(f)} aria-label={`${f.title} klasörünün adını değiştir`}>
                      <Pencil className="mr-1 h-3 w-3" /> Adı Değiştir
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600"
                      disabled={f.total_count > 0 || folderDeleting === f.index}
                      title={f.total_count > 0 ? "İçinde belge bulunan klasör silinemez" : "Boş klasörü sil"}
                      onClick={() => deleteFolder(f)} aria-label={`${f.title} klasörünü sil`}>
                      <Trash2 className="mr-1 h-3 w-3" /> Sil
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="deadlines" className="mt-5"><DeadlinesTab companyId={companyId} /></TabsContent>
        <TabsContent value="users" className="mt-5"><UsersTab companyId={companyId} /></TabsContent>
        <TabsContent value="word" className="mt-5"><WordTab companyId={companyId} /></TabsContent>
      </Tabs>

      <Dialog open={!!folderEditor} onOpenChange={(open) => { if (!open && !folderSaving) setFolderEditor(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{folderEditor?.folder ? "Klasör Adını Değiştir" : "Yeni Klasör Oluştur"}</DialogTitle></DialogHeader>
          <form onSubmit={saveFolder} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="custom-folder-title">Klasör Adı</Label>
              <Input id="custom-folder-title" value={folderTitle} onChange={(e) => setFolderTitle(e.target.value)}
                maxLength={120} required autoFocus placeholder="Örneğin: Denetim Belgeleri" disabled={folderSaving} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" disabled={folderSaving} onClick={() => setFolderEditor(null)}>Vazgeç</Button>
              <Button type="submit" disabled={folderSaving || !folderTitle.trim()} className="bg-emerald-600 hover:bg-emerald-700">
                {folderSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Kaydet
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <FolderDialog companyId={companyId} folder={openFolder} open={!!openFolder}
        onClose={() => setOpenFolder(null)} onChanged={loadFolders} />
    </div>
  );
}

function DeadlinesTab({ companyId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/companies/${companyId}/deadlines`).then((r) => setRows(r.data.rows)); }, [companyId]);
  if (!rows) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-emerald-600" />;
  if (rows.length === 0) return <Card className="p-10 text-center text-slate-400">Yaklaşan veya süresi dolmuş belge yok.</Card>;
  return (
    <Card className="overflow-hidden">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Belge</TableHead><TableHead>Tür</TableHead><TableHead>Son Tarih</TableHead>
          <TableHead>Durum</TableHead><TableHead>E-posta</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.record_id}>
              <TableCell className="font-medium">{r.name}{r.subtype ? <span className="ml-1 text-xs text-slate-400">({r.subtype})</span> : ""}</TableCell>
              <TableCell className="text-sm text-slate-500">{r.type_label}</TableCell>
              <TableCell className="text-sm">{r.deadline}</TableCell>
              <TableCell><StatusBadge status={{ state: r.state, days_left: r.days_left }} /></TableCell>
              <TableCell className="text-xs text-slate-500">{r.email_status || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function UsersTab({ companyId }) {
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/companies/${companyId}/users`).then((r) => setUsers(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  const create = async () => {
    setSaving(true);
    try { await api.post(`/companies/${companyId}/users`, form); toast.success("Müşteri hesabı oluşturuldu"); setOpen(false); setForm({ name: "", email: "", password: "" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const toggle = async (u) => { await api.put(`/users/${u.id}`, { is_active: !u.is_active }); toast.success(u.is_active ? "Pasife alındı" : "Aktifleştirildi"); load(); };
  const [deletingUser, setDeletingUser] = useState(null);
  const deleteUser = async (u) => {
    if (!window.confirm(`"${u.name || u.email}" müşteri hesabı kalıcı olarak silinsin mi? Bu kişi artık giriş yapamayacak. Firmanın belgeleri silinmeyecek.`)) return;
    setDeletingUser(u.id);
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Müşteri hesabı silindi");
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setDeletingUser(null); }
  };
  const resetPw = async (u) => {
    const p = window.prompt(`${u.email} için yeni şifre (min 6):`);
    if (!p) return;
    try { await api.put(`/users/${u.id}`, { password: p }); toast.success("Şifre güncellendi"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!users) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-emerald-600" />;
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setOpen(true)} data-testid="add-user-button"><UserPlus className="mr-2 h-4 w-4" />Müşteri Hesabı Ekle</Button>
      </div>
      {users.length === 0 ? <Card className="p-10 text-center text-slate-400">Bu firmaya bağlı müşteri hesabı yok.</Card> : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Ad</TableHead><TableHead>E-posta</TableHead><TableHead>Durum</TableHead><TableHead className="text-right">İşlem</TableHead></TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.is_active ? <span className="text-emerald-700">Aktif</span> : <span className="text-slate-400">Pasif</span>}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" className="mr-2" onClick={() => resetPw(u)}>Şifre</Button>
                    <Button size="sm" variant="outline" onClick={() => toggle(u)} data-testid={`user-toggle-${u.id}`}>{u.is_active ? "Pasife al" : "Aktifleştir"}</Button>
                    <Button size="sm" variant="outline" className="ml-2 text-red-600" disabled={deletingUser !== null} onClick={() => deleteUser(u)} data-testid={`user-delete-${u.id}`}>
                      <Trash2 className="mr-1 h-4 w-4" />{deletingUser === u.id ? "Siliniyor…" : "Sil"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Müşteri Hesabı Ekle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Ad Soyad</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" className="mt-1" /></div>
            <div><Label>E-posta</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" className="mt-1" /></div>
            <div><Label>Şifre (min 6)</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" className="mt-1" /></div>
          </div>
          <DialogFooter><Button onClick={create} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="user-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Oluştur"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WordTab({ companyId }) {
  const [folders, setFolders] = useState([]);
  const [active, setActive] = useState("all");
  const [files, setFiles] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [uploadFolder, setUploadFolder] = useState("");
  const [saving, setSaving] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  const loadFolders = () => api.get(`/companies/${companyId}/word-folders`).then((r) => setFolders(r.data));
  const loadFiles = () => {
    const params = {};
    if (active !== "all") params.folder_id = active;
    if (q.trim()) params.q = q.trim();
    api.get(`/companies/${companyId}/word-files`, { params }).then((r) => setFiles(r.data));
  };
  useEffect(() => { loadFolders(); /* eslint-disable-next-line */ }, [companyId]);
  useEffect(() => { loadFiles(); /* eslint-disable-next-line */ }, [companyId, active, q]);

  const folderName = (id) => folders.find((f) => f.id === id)?.name || "—";

  const createFolder = async () => {
    if (!newFolderName.trim()) { toast.error("Klasör adı girin"); return; }
    try { await api.post(`/companies/${companyId}/word-folders`, { name: newFolderName }); toast.success("Klasör oluşturuldu"); setNewFolderName(""); setNewFolderOpen(false); loadFolders(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const renameFolder = async (f) => {
    const n = window.prompt("Yeni klasör adı:", f.name);
    if (!n || !n.trim()) return;
    try { await api.put(`/word-folders/${f.id}`, { name: n }); toast.success("Yeniden adlandırıldı"); loadFolders(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteFolder = async (f) => {
    if (!window.confirm(`"${f.name}" klasörü silinsin mi? İçindeki dosyalar Genel klasöre taşınır.`)) return;
    try { await api.delete(`/word-folders/${f.id}`); toast.success("Klasör silindi"); if (active === f.id) setActive("all"); loadFolders(); loadFiles(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const create = async () => {
    if (!name.trim() || !file) { toast.error("Ad ve dosya zorunlu"); return; }
    setSaving(true);
    const fd = new FormData(); fd.append("name", name); fd.append("file", file);
    fd.append("folder_id", uploadFolder || (active !== "all" ? active : ""));
    try { await api.post(`/companies/${companyId}/word-files`, fd); toast.success("Dosya yüklendi"); setOpen(false); setName(""); setFile(null); setUploadFolder(""); loadFolders(); loadFiles(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const addVer = async (w, f) => { const fd = new FormData(); fd.append("file", f); await api.post(`/word-files/${w.id}/version`, fd); toast.success("Yeni sürüm yüklendi"); loadFiles(); };
  const move = async (w, folderId) => { try { await api.post(`/word-files/${w.id}/move`, { folder_id: folderId }); toast.success("Taşındı"); loadFolders(); loadFiles(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const del = async (w) => { if (!window.confirm(`"${w.name}" silinsin mi?`)) return; await api.delete(`/word-files/${w.id}`); toast.success("Silindi"); loadFolders(); loadFiles(); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <p className="text-sm text-amber-800"><FileLock2 className="mr-1.5 inline h-4 w-4" />Bu alan ve tüm alt klasörler yalnızca sizin erişiminizdedir. Müşteriler göremez.</p>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setOpen(true)} data-testid="add-word-button"><Plus className="mr-2 h-4 w-4" />Dosya Yükle</Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setActive("all")} data-testid="word-folder-all"
          className={`rounded-full border px-3 py-1.5 text-sm ${active === "all" ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>Tümü</button>
        {folders.map((f) => (
          <span key={f.id} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm ${active === f.id ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600"}`}>
            <button onClick={() => setActive(f.id)} data-testid={`word-folder-${f.id}`}>{f.name} ({f.file_count})</button>
            <Pencil className="h-3 w-3 cursor-pointer text-slate-400 hover:text-slate-700" onClick={() => renameFolder(f)} data-testid={`word-folder-rename-${f.id}`} />
            {!f.is_default && <Trash2 className="h-3 w-3 cursor-pointer text-slate-400 hover:text-red-600" onClick={() => deleteFolder(f)} />}
          </span>
        ))}
        <Button variant="outline" size="sm" onClick={() => setNewFolderOpen(true)} data-testid="add-word-folder-button"><FolderPlus className="mr-1 h-4 w-4" />Yeni Klasör</Button>
        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Dosya ara..." className="w-56 pl-8" data-testid="word-search-input" />
        </div>
      </div>

      {files === null ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-emerald-600" />
        : files.length === 0 ? <Card className="p-10 text-center text-slate-400">Bu görünümde dosya yok.</Card> : (
        <div className="space-y-3">
          {files.map((w) => (
            <Card key={w.id} className="p-4" data-testid={`word-item-${w.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-semibold text-[#0a192f]">{w.name}</div>
                  <div className="text-xs text-slate-400">{w.current ? w.current.filename : "—"} · Klasör: {folderName(w.folder_id)}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {w.current && <Button size="sm" variant="outline" onClick={() => openFile(`/word-versions/${w.current.version_id}/download`, true, w.current.filename)} data-testid={`word-download-${w.id}`}><Download className="mr-1 h-4 w-4" />İndir</Button>}
                  <label className="inline-flex">
                    <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx" className="hidden" onChange={(e) => e.target.files[0] && addVer(w, e.target.files[0])} />
                    <span className="inline-flex cursor-pointer items-center rounded-md border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50"><Upload className="mr-1 h-4 w-4" />Yeni Sürüm</span>
                  </label>
                  <Select value={w.folder_id || ""} onValueChange={(v) => move(w, v)}>
                    <SelectTrigger className="h-9 w-40" data-testid={`word-move-${w.id}`}><ArrowRightLeft className="mr-1 h-3.5 w-3.5" /><SelectValue placeholder="Taşı" /></SelectTrigger>
                    <SelectContent>{folders.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => del(w)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              {w.versions.length > 1 && (
                <div className="mt-3 rounded-lg bg-slate-50 p-3 text-xs">
                  <p className="mb-1 flex items-center gap-1 font-semibold text-slate-600"><History className="h-3.5 w-3.5" />Sürümler</p>
                  {w.versions.map((v) => (
                    <div key={v.id} className="flex items-center justify-between">
                      <span className={v.is_current ? "font-semibold text-emerald-700" : "text-slate-500"}>v{v.version_no} · {v.filename}</span>
                      <button onClick={() => openFile(`/word-versions/${v.id}/download`, true, v.filename)} className="text-emerald-700 hover:underline">İndir</button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Dosya Yükle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Dosya Adı / Konu</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="word-name-input" className="mt-1" /></div>
            <div><Label>Klasör</Label>
              <Select value={uploadFolder || (active !== "all" ? active : "")} onValueChange={setUploadFolder}>
                <SelectTrigger className="mt-1" data-testid="word-folder-select"><SelectValue placeholder="Genel" /></SelectTrigger>
                <SelectContent>{folders.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Dosya (PDF / Word / Excel)</Label><Input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx" onChange={(e) => setFile(e.target.files[0])} data-testid="word-file-input" className="mt-1" /></div>
          </div>
          <DialogFooter><Button onClick={create} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="word-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yükle"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Yeni Klasör</DialogTitle></DialogHeader>
          <div><Label>Klasör Adı</Label><Input value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} data-testid="word-folder-name-input" className="mt-1" /></div>
          <DialogFooter><Button onClick={createFolder} className="bg-emerald-600 hover:bg-emerald-700" data-testid="word-folder-save-button">Oluştur</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
